import React, { useState } from 'react';
import './ZipAnalysisView.css';

interface FileDetails {
  path: string;
  size: number;
  type: 'image' | 'script' | 'stylesheet' | 'document' | 'other';
  optimized: boolean;
  savingsPercent?: number;
}

interface ZipAnalysisViewProps {
  files: FileDetails[];
}

const getFileIcon = (type: string) => {
  switch (type) {
    case 'image': return '🖼️';
    case 'script': return '📄';
    case 'stylesheet': return '🎨';
    case 'document': return '📄';
    default: return '📄';
  }
};

const getFileTypeColor = (type: string) => {
  switch (type) {
    case 'image': return '#4CAF50';
    case 'script': return '#2196F3';
    case 'stylesheet': return '#9C27B0';
    case 'document': return '#FF9800';
    default: return '#757575';
  }
};

export const ZipAnalysisView: React.FC<ZipAnalysisViewProps> = ({ files }) => {
  const [sectionExpanded, setSectionExpanded] = useState(false);

  // Auto-expand all folders by default
  const getAllFolderPaths = (fileList: FileDetails[]): Set<string> => {
    const paths = new Set<string>();
    fileList.forEach(f => {
      const parts = f.path.split('/');
      for (let i = 1; i < parts.length; i++) {
        paths.add(parts.slice(0, i).join('/'));
      }
    });
    return paths;
  };
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(() => getAllFolderPaths(files));
  
  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  const maxSize = Math.max(...files.map(f => f.size));

  // Build hierarchical folder structure
  type FileNode = {
    name: string;
    type: 'file' | 'folder';
    file?: FileDetails;
    children?: FileNode[];
    path: string;
  };

  const buildTree = (): FileNode => {
    const root: FileNode = { name: '', type: 'folder', children: [], path: '' };
    
    files.forEach(file => {
      const parts = file.path.split('/');
      let current = root;
      
      // Navigate/create folders
      for (let i = 0; i < parts.length - 1; i++) {
        const folderName = parts[i];
        const folderPath = parts.slice(0, i + 1).join('/');
        
        let folder = current.children!.find(c => c.name === folderName && c.type === 'folder');
        if (!folder) {
          folder = { name: folderName, type: 'folder', children: [], path: folderPath };
          current.children!.push(folder);
        }
        current = folder;
      }
      
      // Add file
      const fileName = parts[parts.length - 1];
      current.children!.push({
        name: fileName,
        type: 'file',
        file,
        path: file.path
      });
    });
    
    return root;
  };

  const fileTree = buildTree();

  const toggleFolder = (folderPath: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderPath)) {
      newExpanded.delete(folderPath);
    } else {
      newExpanded.add(folderPath);
    }
    setExpandedFolders(newExpanded);
  };

  const renderNode = (node: FileNode, indent: number = 0): React.ReactNode => {
    if (node.type === 'file' && node.file) {
      const file = node.file;
      const type = file.type || 'other';
      const optimized = file.optimized ?? true;
      const percentage = (file.size / maxSize) * 100;

      return (
        <tr key={file.path} className="file-row">
          <td className="file-path" style={{ paddingLeft: `${indent * 20 + 20}px` }}>
            <span className="file-icon">{getFileIcon(type)}</span>
            <span>{node.name}</span>
          </td>
          <td className="file-size">{(file.size / 1024).toFixed(2)} KB</td>
          <td className="file-optimization">
            {type === 'image' ? (
              optimized ? (
                <span className="status-optimized">✓ Optimized</span>
              ) : (
                file.savingsPercent ? (
                  <span className="status-not-optimized">
                    ⚠ Not optimized <strong>({file.savingsPercent}%)</strong>
                  </span>
                ) : (
                  <span className="status-not-optimized">⚠ Not optimized</span>
                )
              )
            ) : (
              <span className="status-na">-</span>
            )}
          </td>

          <td className="file-bar">
            <div 
              className="size-bar" 
              style={{ 
                width: `${percentage}%`,
                backgroundColor: getFileTypeColor(type)
              }}
            />
          </td>
        </tr>
      );
    }
    
    // Folder
    if (node.type === 'folder' && node.children && node.children.length > 0) {
      const isExpanded = expandedFolders.has(node.path);
      const fileCount = node.children.filter(c => c.type === 'file').length;
      
      return (
        <React.Fragment key={node.path}>
          <tr 
            className="folder-row" 
            onClick={() => toggleFolder(node.path)}
            style={{ cursor: 'pointer' }}
          >
            <td colSpan={4} style={{ paddingLeft: `${indent * 20}px` }}>
              <span className="folder-icon">
                {isExpanded ? '📂' : '📁'}
              </span>
              <strong>{node.name}/</strong>
              <span style={{ marginLeft: '10px', color: '#666' }}>
                ({fileCount} files)
              </span>
            </td>
          </tr>
          {isExpanded && node.children.map(child => renderNode(child, indent + 1))}
        </React.Fragment>
      );
    }
    
    return null;
  };

  return (
    <div className="zip-analysis-view">
      <div className="zip-header" onClick={() => setSectionExpanded(!sectionExpanded)} style={{ cursor: 'pointer' }}>
        <h3>ZIP Archive Analysis</h3>
        <div className="zip-stats">
          <span>{files.length} files</span>
          <span>{(totalSize / 1024).toFixed(2)} KB total</span>
          <span className="expand-icon">{sectionExpanded ? "\u25BC" : "\u25B6"}</span>
        </div>
      </div>
      {sectionExpanded && <table className="zip-file-table">
        <thead>
          <tr>
            <th>File</th>
            <th>Size</th>
            <th>Optimization</th>
            <th style={{ width: '200px' }}>Relative Size</th>
          </tr>
        </thead>
        <tbody>
          {fileTree.children && fileTree.children.map(node => renderNode(node, 0))}
        </tbody>
      </table>}
    </div>
  );
};